import {useRef, useState} from 'react';
import {
    hashQueryKey,
    QueryKey,
    useQuery,
    useQueryClient,
    UseQueryOptions,
    UseQueryResult,
} from 'react-query';

export default function useDebouncedQuery<T extends (...args: any) => any>(
    delay: number,
    queryKey: QueryKey,
    queryFn: T,
    queryOptions: Omit<UseQueryOptions, 'enabled' | 'queryHash' | 'queryHashFn'>,
): UseQueryResult<Exclude<Awaited<ReturnType<T>>, never[]>, unknown> {
    const queryClient = useQueryClient();

    const nextHash = useRef<string | null>(null);
    const timeoutId = useRef<NodeJS.Timeout | null>(null);

    const [hash, setHash] = useState(() => hashQueryKey(queryKey));

    nextHash.current = hashQueryKey(queryKey);

    // This behaves like a useEffect with hashQueryKey(queryKey) in deps but simpler
    if (nextHash.current !== hash) {
        if (timeoutId.current !== null) {
            queryClient.cancelQueries([hash]);
            clearTimeout(timeoutId.current);
            timeoutId.current = null;
        }

        timeoutId.current = setTimeout(() => {
            timeoutId.current = null;
            setHash(nextHash.current as string);
        }, delay);
    }

    const query = useQuery([hash], queryFn, queryOptions);

    // @ts-ignore
    return {
        ...query,
        isLoading: query.isLoading || nextHash.current !== hash,
        isFetching: query.isFetching || nextHash.current !== hash,
    };
}
